<script setup lang="ts">
/**
 * Plays any library item the way a screen would: an image, a video, a built-in app (live), a campaign (each section
 * runs its own timeline) or a playlist (its items in order, campaigns included). `full` = the lightbox: video
 * controls with sound, and a play / scrub bar for campaigns and playlists. Inline (Information Panel) everything is
 * muted and loops. `bare` = no inline buttons (a card's hover peek). Custom HTML5 apps run code, so they only show their icon here.
 */
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { Layout, MediaAssetRecord, Playlist } from '@codot/contracts';
import PreviewMedia from '../apps/PreviewMedia.vue';
import ZoneWidget from '../apps/ZoneWidget.vue';
import PdfPagePlayer from './PdfPagePlayer.vue';
import CompositionThumb from '../composition/CompositionThumb.vue';
import { formatLength } from '../../utils/format';

export type PreviewTarget = { kind: 'asset'; asset: MediaAssetRecord } | { kind: 'campaign'; campaign: Layout } | { kind: 'playlist'; playlist: Playlist };

const { t } = useI18n();
const props = withDefaults(
  defineProps<{ target: PreviewTarget; assets: Map<string, MediaAssetRecord>; campaigns: Map<string, Layout>; full?: boolean; bare?: boolean }>(),
  { full: false, bare: false },
);

const DEFAULT_MS = 10_000;
const itemMs = (durationMs: number | null, assetId: string) => durationMs ?? props.assets.get(assetId)?.durationMs ?? DEFAULT_MS;

/** A campaign section's timeline: [asset, start, length] in order, and its total. */
function zoneTimeline(z: Layout['zones'][number]) {
  let at = 0;
  const parts = z.items.map((i) => {
    const len = itemMs(i.durationMs, i.assetId);
    const p = { assetId: i.assetId, start: at, len, fit: i.fit };
    at += len;
    return p;
  });
  return { parts, total: at };
}
/** What each section of a campaign shows `ms` into the campaign. */
function campaignFrame(c: Layout, ms: number) {
  return [...c.zones]
    .sort((a, b) => a.zIndex - b.zIndex)
    .map((z) => {
      const tl = zoneTimeline(z);
      if (!tl.total) return { z, asset: null, offset: 0, fit: c.fit ?? 'fit' };
      const local = z.loop ? ms % tl.total : ms;
      const part = tl.parts.find((p) => local >= p.start && local < p.start + p.len);
      return { z, asset: part ? (props.assets.get(part.assetId) ?? null) : null, offset: part ? local - part.start : 0, fit: part?.fit ?? c.fit ?? 'fit' };
    });
}

/* ---- the clock (campaigns and playlists) ---- */
const playlistItems = computed(() => (props.target.kind === 'playlist' ? props.target.playlist.items.filter((i) => !i.paused) : []));
const total = computed(() => {
  if (props.target.kind === 'campaign') return props.target.campaign.durationMs;
  if (props.target.kind === 'playlist') return playlistItems.value.reduce((s, i) => s + i.effectiveDurationMs, 0);
  return 0;
});
const clockT = ref(0);
const playing = ref(true);
let raf = 0;
let last = 0;
function frame(now: number): void {
  if (last && playing.value && total.value) clockT.value = (clockT.value + (now - last)) % total.value;
  last = now;
  raf = requestAnimationFrame(frame);
}
onMounted(() => (raf = requestAnimationFrame(frame)));
onBeforeUnmount(() => cancelAnimationFrame(raf));
watch(
  () => props.target,
  () => {
    clockT.value = 0;
    playing.value = true;
  },
);

/** The playlist item at the clock, and how far into it. */
const playlistNow = computed(() => {
  let at = 0;
  for (const i of playlistItems.value) {
    if (clockT.value < at + i.effectiveDurationMs) return { item: i, offset: clockT.value - at };
    at += i.effectiveDurationMs;
  }
  return null;
});
const stage = computed(() => {
  if (props.target.kind === 'campaign') return { campaign: props.target.campaign, offset: clockT.value };
  const now = playlistNow.value;
  if (!now) return null;
  if (now.item.content.kind === 'campaign') {
    const c = props.campaigns.get(now.item.content.id);
    return c ? { campaign: c, offset: now.offset } : null;
  }
  const a = props.assets.get(now.item.content.id);
  return a ? { asset: a, offset: now.offset, fit: now.item.fit ?? 'fit' } : null;
});
/** The campaign on stage now (with how far into it), or the playlist's asset on stage now. */
const stageCampaign = computed(() => (stage.value && 'campaign' in stage.value ? (stage.value as { campaign: Layout; offset: number }) : null));
const stageAsset = computed(() =>
  stage.value && 'asset' in stage.value ? (stage.value as { asset: MediaAssetRecord; offset: number; fit: 'fit' | 'fill' | 'stretch' }) : null,
);

/* ---- a single asset ---- */
const asset = computed(() => (props.target.kind === 'asset' ? props.target.asset : null));
const muted = ref(!props.full);
/** The file couldn't be played (missing or unsupported): show its thumbnail instead. */
const broken = ref(false);
watch(asset, () => (broken.value = false));


const timed = computed(() => props.target.kind !== 'asset');
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <div class="relative min-h-0 flex-1 overflow-hidden bg-black">
      <!-- Single asset -->
      <template v-if="asset">
        <video
          v-if="asset.kind === 'video' && asset.fileUrl && !broken"
          :key="asset.id"
          :src="asset.fileUrl"
          :poster="asset.thumbnailUrl ?? undefined"
          class="h-full w-full object-contain"
          :controls="full"
          :muted="muted"
          autoplay
          loop
          playsinline
          :aria-label="t('content.previewOf', { name: asset.name })"
          @error="broken = true"
        />
        <div v-else-if="asset.kind === 'video' && broken" class="relative h-full w-full">
          <img v-if="asset.thumbnailUrl" :src="asset.thumbnailUrl" alt="" class="h-full w-full object-contain opacity-60" />
          <p class="absolute inset-x-0 bottom-3 text-center text-xs text-white">{{ t('content.pvCantPlay') }}</p>
        </div>
        <div v-else-if="asset.kind === 'audio' && asset.fileUrl" class="flex h-full flex-col items-center justify-center gap-3 px-4">
          <span class="text-4xl" aria-hidden="true">🎵</span>
          <audio :key="asset.id" :src="asset.fileUrl" controls :autoplay="full" class="w-full max-w-md" />
        </div>
        <CompositionThumb v-else-if="asset.kind === 'composition' && asset.composition" :key="asset.id" :doc="asset.composition.doc" playing />
        <PdfPagePlayer v-else-if="asset.kind === 'pdf' && asset.pdf?.pages.length" :key="asset.id" :pages="asset.pdf.pages" :page-ms="asset.pdf.pageMs" :transition="asset.pdf.pageTransition" class="bg-slate-200 dark:bg-slate-800" />
        <ZoneWidget v-else-if="asset.config" :config="asset.config" :playing="true" :width="1920" :height="1080" class="absolute inset-0" />
        <img
          v-else-if="(asset.kind === 'image' && asset.fileUrl) || asset.thumbnailUrl"
          :src="(asset.kind === 'image' ? asset.fileUrl : asset.thumbnailUrl)!"
          :alt="t('content.previewOf', { name: asset.name })"
          class="h-full w-full object-contain"
        />
        <div v-else class="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-slate-300">
          <span class="text-4xl" aria-hidden="true">{{ asset.kind === 'app' ? '🧩' : asset.kind === 'audio' ? '🎵' : '🖼️' }}</span>
          <span v-if="asset.kind === 'app'">{{ t('content.pvCustomApp') }}</span>
          <span v-else-if="asset.status !== 'READY'">{{ t('content.pvNotReady') }}</span>
        </div>
      </template>

      <!-- Campaign (directly, or the playlist item playing now) -->
      <template v-else-if="stageCampaign">
        <div class="absolute inset-0 flex items-center justify-center" style="container-type: size">
          <!-- As large as fits: by width, or by height for a taller canvas (container units). -->
          <div
            class="relative"
            :style="{
              aspectRatio: `${stageCampaign.campaign.canvasWidth} / ${stageCampaign.campaign.canvasHeight}`,
              width: `min(100cqw, ${(100 * stageCampaign.campaign.canvasWidth) / stageCampaign.campaign.canvasHeight}cqh)`,
            }"
          >
            <div
              v-for="f in campaignFrame(stageCampaign.campaign, stageCampaign.offset)"
              :key="f.z.id"
              class="absolute overflow-hidden"
              :style="{
                left: `${(f.z.x / stageCampaign.campaign.canvasWidth) * 100}%`,
                top: `${(f.z.y / stageCampaign.campaign.canvasHeight) * 100}%`,
                width: `${(f.z.width / stageCampaign.campaign.canvasWidth) * 100}%`,
                height: `${(f.z.height / stageCampaign.campaign.canvasHeight) * 100}%`,
                zIndex: f.z.zIndex,
              }"
            >
              <PreviewMedia v-if="f.asset" :asset="f.asset" :offset-ms="f.offset" :playing="playing" :width="f.z.width" :height="f.z.height" :fit="f.fit" />
            </div>
          </div>
        </div>
      </template>

      <!-- Playlist item that is an asset -->
      <PreviewMedia v-else-if="stageAsset" :key="stageAsset.asset.id" :asset="stageAsset.asset" :offset-ms="stageAsset.offset" :playing="playing" :width="1920" :height="1080" :fit="stageAsset.fit" />
      <div v-else-if="timed" class="flex h-full items-center justify-center text-sm text-slate-400">{{ t('content.pvEmpty') }}</div>

      <!-- Inline controls -->
      <div v-if="!full && !bare" class="absolute bottom-1.5 start-1.5 flex gap-1">
        <button
          v-if="timed"
          type="button"
          class="rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
          :aria-label="playing ? t('content.pvPause') : t('content.pvPlay')"
          @click.stop="playing = !playing"
        >
          {{ playing ? '❚❚' : '▶' }}
        </button>
        <button
          v-if="asset?.kind === 'video'"
          type="button"
          class="rounded bg-black/60 px-1.5 py-0.5 text-xs text-white hover:bg-black/80"
          :aria-label="muted ? t('content.pvUnmute') : t('content.pvMute')"
          @click.stop="muted = !muted"
        >
          {{ muted ? '🔇' : '🔊' }}
        </button>
      </div>
    </div>

    <!-- Lightbox timeline (campaigns and playlists) -->
    <div v-if="full && timed && total" class="flex items-center gap-3 bg-slate-900 px-3 py-2 text-xs text-slate-200">
      <button type="button" class="w-6 text-base" :aria-label="playing ? t('content.pvPause') : t('content.pvPlay')" @click="playing = !playing">{{ playing ? '❚❚' : '▶' }}</button>
      <input v-model.number="clockT" type="range" min="0" :max="Math.max(1, total - 1)" class="flex-1 accent-indigo-500" :aria-label="t('content.pvPosition')" />
      <span class="tabular-nums">{{ formatLength(clockT) }} / {{ formatLength(total) }}</span>
      <span v-if="target.kind === 'playlist' && playlistNow" class="hidden max-w-48 truncate sm:inline">{{ playlistNow.item.name }}</span>
    </div>
  </div>
</template>
